﻿import { store, store$ } from '@redux/store';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  tap,
  distinctUntilKeyChanged,
  fromEvent,
} from 'rxjs';
import { paintMarker, removeMarkers, updateMarkersVisibility } from 'libraries/mapLibrary';
import uiStateSlice from 'layout/redux/uiStateSlice';
import { LocationType, ResultType, searchParametersActions, selectResultType } from 'features/searchResultsFacets';
import { selectIsMarkerAutoScrollFeatureEnabled, selectLayoutBreakpoint } from 'layout/redux/selectors';
import LayoutBreakpoint from 'layout/enums/layoutBreakpoint';
import {
  isFetchingResults,
  areMarkersAvailable,
  areMapBoundariesDefined,
  geoCoordinates,
  mapMarkers,
  selectHoveredMarker,
  selectGeoCoordinatesAndLocation,
  selectedMarker,
  selectedNearbyListingCarousel,
  selectedMarkerAndPage,
  selectFocusedListingAndSitePlans,
  selectSitePlans,
} from '../redux/selectors';
import { mapStateSlice } from '../redux/slices';
import {
  factsSearchParameters,
  featuresSearchParameters,
  locationSearchParameters,
  productSearchParameters,
  isNotCommunityLocationType,
} from '../constants/searchParametersConstants';
import { mapContainerClicked$, mapContainerDoubleClicked$ } from '../redux/observables';
import { isUserInteractingWithSitePlan } from '../services/sitePlan';
import getClassesToApply from '../services/unitMarkerService';

const { actions } = mapStateSlice;
const mapContainer = document.querySelector('#nhs_map');

/**
 * It takes a marker object as an argument, and dispatches an action to the Redux store that sets the
 * selectedMarker property of the state to the marker object
 * @param marker - The marker object that was clicked on.
 */
const setActiveMarker = (marker) => {
  if (!marker) {
    store.dispatch(actions.selectedMarker(marker));
    return;
  }

  /*
   * The selectedMarker in state needs the page number,
   * then before to dispach the selectedMarker is necesary set it the page number
   * from the mapMarker state.
   */
  const newSelectedMarker = Object.assign({}, marker);
  const state = store.getState();
  const { pageNumber, isFeaturedListing } = mapMarkers(state).find((housing) => housing.id === newSelectedMarker.id);
  newSelectedMarker.pageNumber = pageNumber;
  newSelectedMarker.isFeaturedListing = !!isFeaturedListing;
  store.dispatch(actions.selectedMarker(newSelectedMarker));

  document.body.dispatchEvent(
    new CustomEvent('markerPinCLicked', {
      detail: {
        marker: newSelectedMarker,
      },
    })
  );
};

/**
 * It takes a marker object as an argument, and dispatches an action to the Redux store that sets the
 * selectedMarker property of the state to the marker object
 * @param marker - The marker object that was clicked on.
 */
const setActiveNearbyListingsCarousel = (listings) => store.dispatch(actions.setActiveNearbyListingsCarousel(listings));

/**
 * setHoveredMarker: this functions stores the hovered marker so other flows interested on it can react and
 * act accordingly given a new marker hovered.
 * @param {markerInformation} markerInformation is an object with the marker itself and tooltip of the hovered marker.
 * @returns
 */
const setHoveredMarker = (markerInformation) => {
  if (markerInformation) {
    markerInformation.clonedMarker = markerInformation.marker.cloneNode(true);
    markerInformation.clonedTooltip = markerInformation.tooltip.cloneNode(true);
  }

  store.dispatch(actions.hoveredMarker(markerInformation));
};

/* Dispatching an action to the store to fetch the markers. */
const fetchResults = () => {
  store.dispatch(
    actions.fetchResults({
      isFetchingResults: true,
      parameters: store.getState().searchParameters,
    })
  );
};

/**
 * hideNotMainTooltips: hides all the not main tooltips of a marker.
 * Since a single marker can represent several other markers sharing a nearby area, that single marker
 * is responsible of having the tooltips of all the other markers it represent. When an user is not
 * hovering any card, then we make sure to keep not hidden just the main tooltip of the marker in case
 * the user hovers that marker directly in the map.
 * @param {Array} tooltipArray representing the tooltips a single marker represents.
 */
const hideNotMainTooltips = (tooltipArray) => {
  tooltipArray.forEach((tooltip) => tooltip.setAttribute('hidden', ''));
  if (tooltipArray[0]) {
    tooltipArray[0].removeAttribute('hidden');
  }
};

/**
 * Removing active tooltip class and the hovered.
 */
const removeMarkerHovered = () => {
  const markerIcon = document.querySelector(`[data-marker-icon][data-hovered="true"]`);
  if (!markerIcon) {
    return;
  }
  markerIcon.closest('[data-geographic-key]').removeAttribute('data-marker-hovered');
  markerIcon.classList.remove('nhs-mq-marker-hovered--large');
  delete markerIcon.dataset.hovered;

  const markerElement = markerIcon?.closest('[data-marker-element]');
  markerElement?.classList.remove('marker--active');
};

/**
 * Removing the the tooltip when the summary card link is clicked.
 */
fromEvent(document, 'NearbyListingsCardLinkClick').subscribe(removeMarkerHovered);

/**
 * Removing the active class to marker to close the marker card.
 */
const closeMarkerCard = () => {
  const markerIcon = document.querySelector(`[data-marker-icon][data-selected]`);
  if (!markerIcon) {
    return;
  }

  const markerElement = markerIcon.closest('[data-marker-element]');
  removeMarkerHovered();
  const { selected } = getClassesToApply({
    isBasic: markerIcon.dataset.isBasic === 'true',
    isNoPrice: markerIcon.dataset.isNoPrice === 'true',
    isSpotlight: markerIcon.dataset.isSpotlight === 'true',
    isSelected: true,
    isPriceReduction: markerIcon.dataset.isPriceReduction === 'true',
  });
  selected.forEach((className) => markerIcon.classList.remove(className));
  delete markerIcon.dataset.selected;
  markerElement?.classList.remove('active');
  markerElement?.classList?.remove('marker--active');
  hideNotMainTooltips(markerElement?.querySelectorAll('[data-tooltip]'));
  setHoveredMarker(null);
};

/**
 * A function that check if the marker is the active marker.
 */
const isMarkerActive = (marker) => {
  const mapState = store?.getState()?.mapState;
  return marker.id === mapState?.selectedMarker?.id;
};

/**
 * A function that handler markers according to the user interaction of the map.
 */
const handleMarkerMapChanges = () => {
  const state = store.getState();
  const isMarkerSelected = !!state.mapState.selectedMarker;
  const carousel = selectedNearbyListingCarousel(state);

  if (isMarkerSelected) {
    setActiveMarker(null);
  }

  if (carousel) {
    setActiveNearbyListingsCarousel(null);
  }

  closeMarkerCard();
};

/**
 * Add the active class to markert to open the marker card.
 */
const openMarkerCard = (marker) => {
  marker.closest('[data-geographic-key]').setAttribute('data-marker-hovered', true);
  marker.classList?.add('nhs-mq-marker-hovered--large');
  const { selected, viewed } = getClassesToApply({
    isBasic: marker.dataset.isBasic === 'true',
    isNoPrice: marker.dataset.isNoPrice === 'true',
    isSpotlight: marker.dataset.isSpotlight === 'true',
    isSelected: true,
    isViewed: true,
  });
  selected.forEach((className) => marker.classList.add(className));
  viewed.forEach((className) => marker.classList.add(className));
  marker.dataset.selected = true;
  marker.dataset.hovered = true;
  const markerElement = marker?.closest('[data-marker-element]');
  markerElement?.classList?.add('active');
  markerElement?.classList?.add('marker--active');
};

/**
 * Opening in new tab the url of the marker.
 */
const openUrl = (marker, event) => {
  const interactionType = event.type === 'dblclick' ? 'double_click' : 'slow_double_click';

  // emit custom event with marker data
  const customEvent = new CustomEvent('openMarkerUrl', {
    detail: {
      marker,
      interactionType,
    },
  });

  document.body.dispatchEvent(customEvent);

  const url = marker.url;
  window.open(url, '_blank');
};

/**
 * A function that is called when a marker is clicked.
 *
 * Used to emit a new action set the selectedMarker to the currently active marker object.
 */
const onMarkerClicked = (marker, event) => {
  if (event.domEvent.srcElement.hasAttribute("data-interactive-plan-button")) {
    return;
  }
  
  const googleAnalyticsDTO = marker.googleAnalyticsClickEvent;
  store.dispatch(uiStateSlice.actions.trackUserEvent({ googleAnalyticsDTO }));
  if (isMarkerActive(marker)) {
    openUrl(marker, event);
    removeMarkerHovered();
  }

  setActiveMarker(marker);
};

/**
 * adjustPreviousHoveredMarkerStyle: when a new marker is hovered, this function finds the previous hovered
 * marker (if any) and returns it to the previous styles it had.
 * @param {Object} previousMarkerInfo from the store; it is the previous look and feel of the current hovered marker.
 */
const adjustPreviousHoveredMarkerStyle = ({ previousMarkerInfo }) => {
  if (!previousMarkerInfo) {
    return;
  }

  previousMarkerInfo.marker.innerHTML = previousMarkerInfo.clonedMarker.innerHTML;
  previousMarkerInfo.marker.classList = previousMarkerInfo.clonedMarker.classList;
  hideNotMainTooltips(previousMarkerInfo.marker.closest('[data-marker-element]').querySelectorAll('[data-tooltip]'));
};

/**
 * adjustCurrentHoveredMarkerStyle: when a new marker is hovered, this function adjust its look and feel so
 * it represents the information of the hovered listing in an accurate way.
 * @param {Object} currentMarkerInfo is the information of the current hovered marker.
 * @returns
 */
const adjustCurrentHoveredMarkerStyle = ({ currentMarkerInfo }) => {
  if (!currentMarkerInfo) {
    return;
  }

  currentMarkerInfo.marker.innerHTML =
    currentMarkerInfo?.tooltip?.querySelector('[data-marker-clone] div')?.outerHTML || '';
  currentMarkerInfo.marker.classList = '';
  currentMarkerInfo.marker.classList.add(...currentMarkerInfo.tooltip.dataset.markerStyles.split(','));
};

/**
 * prioritizeMarkerTooltip: this function prioritizes the marker tooltip to show once a card gets hovered.
 * Marker found could not be the one the user is interested since a marker in the map can represent several listings,
 * so we make sure that the marker found represents the corresponding listing of the user's point of interest.
 * If the marker found is not the same as the user is interested in seeing, then we make sure to set the tooltip
 * that represents the interested marker information.
 */
const prioritizeMarkerTooltip = ({ marker, tooltip }) => {
  const focusedListingTooltips = marker.closest('[data-marker-element]').querySelectorAll('[data-tooltip]');
  focusedListingTooltips.forEach((card) => card.setAttribute('hidden', ''));
  tooltip?.removeAttribute('hidden');
};

/**
 * hasExternalRepresentation function will calculate if the marker we are creating has a representation already in
 * the map, for instance: a site plan.
 * @param {Object} housingLocation the marker to be created.
 * @returns a flag indicating wheather the marker has a representation already in the map.
 */
const hasExternalRepresentation = (housingLocation) => {
  const state = store.getState();
  const sitePlans = selectSitePlans(state);
  return sitePlans.some((sitePlan) =>
    sitePlan.communityId === housingLocation.communityId);
}

/**
 * CreateMarker takes a housingLocation object and returns a new object with a markerPosition property
 * that has a lat and lng property.
 * @param housingLocation - the object that contains the Lat and Lng properties
 */
const configureMarker = (housingLocation) => ({
  events: {
    click: onMarkerClicked,
  },
  googleAnalyticsClickEvent: housingLocation.getGoogleAnalyticsEventForClicks(),
  icon: housingLocation.getIcon(),
  id: housingLocation.id,
  markerPosition: {
    lat: +housingLocation.latitude,
    lng: +housingLocation.longitude,
  },
  type: housingLocation.getType(),
  metaData: housingLocation.getHousingLocationMetaData(),
  zIndex: housingLocation.zIndex,
  url: housingLocation.getDetailUrl(),
  description: housingLocation.getDescription(),
  ignoreVisibilityUpdates: hasExternalRepresentation(housingLocation),
});

/**
 * It takes the current markers and the new markers, and returns an object with two arrays: one with
 * the markers to add, and one with the markers to remove.
 *
 * The setTimeout is to allow the browser to split the task improving the performance.
 */
const updateMarkers = (markersFromStore) => {
  const markers = markersFromStore.map(configureMarker);
  setTimeout(() => paintMarker(markers));
  setTimeout(() => updateMarkersVisibility());
};

/**
 * requiresMarkerVisibilityChange determines if markers need to update their visibility, meaning: if we need to go
 * over ther markers to show/hide them based on the map viewport.
 * Function will return true if one of the following conditions happens:
 * * User has not changed map location, however the map changed its center, ie: user is dragging the map around.
 * * User is searching within map.
 * @param stateTransition - contains the output of a pairwise operation that brings previous and current state values.
 */
const requiresMarkerVisibilityChange = (stateTransition) => {
  const previousState = stateTransition[0];
  const currentState = stateTransition[1];
  return (
    (previousState.location === currentState.location && previousState.coordinates !== currentState.coordinates) ||
    currentState.location.locationType === LocationType.RectangularArea.toString()
  );
};

/**
 * Handles the selected marker by adding or removing the 'marker--active' class.
 * @param {Object} marker - The marker object.
 */
function handleSelectedMarker(marker) {
  const pastActive = document.querySelector('.marker--active');

  if (pastActive) {
    pastActive.classList.remove('marker--active');
  }

  if (marker) {
    const markers = document.querySelectorAll(`[data-id="${marker.id}"][data-type="${marker.type}"]`);

    markers.forEach((icon) => {
      const container = icon?.closest('[data-marker-element]');
      container?.classList.add('marker--active');
    });
  }
}

/**
 * Handles a double click event on a map marker element.
 * @param {Event} event - The double click event object.
 */
function handleMarkerMapDoubleClick(event) {
  const state = store.getState()?.mapState;
  const marker = state?.selectedMarker;

  openUrl(marker, event);
  removeMarkerHovered();
}

/* 
Subscribe to the store$ observable to listen for changes. When markers are available, map the results
property and compare if the value has changed from the previous execution. Then filters to see if the 
map boundaries are defined. 

This subscription handles the scenario when the results have changed.
 */
store$
  .pipe(filter(areMarkersAvailable), filter(areMapBoundariesDefined), map(mapMarkers), distinctUntilChanged())
  .subscribe((markers) => {
    const state = store.getState();
    const carousel = selectedNearbyListingCarousel(state);

    setTimeout(removeMarkers, 0);
    setTimeout(updateMarkers.bind(null, markers), 0);

    // only clear the summary card if available.
    if (carousel) {
      setActiveNearbyListingsCarousel(null);
    }
  });

/* 
Subscribe to the store$ observable to listen for changes. When markers the map boundaries are available, 
map the coordinates property and compare if the value has changed from the previous execution.
Then filters to see if there are results defined. 

This subscription handles the scenario when the map coordinates have changed.
 */
store$
  .pipe(
    filter(areMapBoundariesDefined),
    filter((state) => !isFetchingResults(state)),
    filter(areMarkersAvailable),
    map(selectGeoCoordinatesAndLocation),
    distinctUntilKeyChanged('coordinates'),
    pairwise(),
    filter(requiresMarkerVisibilityChange)
  )
  .subscribe(updateMarkersVisibility);

/** Subscribe to the store$ observable to listen for changes
 * When search parameters in facts leaf change, fetch the new results for map.
 */
const facts$ = store$.pipe(map(factsSearchParameters), distinctUntilChanged());

/** Subscribe to the store$ observable to listen for changes
 * When search parameters in features leaf change, fetch the new results for map.
 */
const features$ = store$.pipe(map(featuresSearchParameters), distinctUntilChanged());

/** Subscribe to the store$ observable to listen for changes
 * When search parameters in location leaf change, fetch the new results for map.
 */
const location$ = store$.pipe(
  filter(isNotCommunityLocationType),
  map(locationSearchParameters),
  distinctUntilChanged()
);

/** Subscribe to the store$ observable to listen for changes
 * When search parameters in product leaf change, fetch the new results for map.
 */
const product$ = store$.pipe(map(productSearchParameters), distinctUntilChanged());

/**
 * If some stream has changed, fetch the results.
 * Don't remove the debounceTime because it helps to prevent duplicate the fetch result request if
 * some leaf change at the same time.
 */
combineLatest([facts$, features$, location$, product$]).pipe(debounceTime(100)).subscribe(fetchResults);

/**
 * Subscribing to the store$ observable to listen for changes.
 * When the selectedMarker changes, map the selectedMarker and compare if the value has changed to valid value.
 * If that is the case run the clearActiveMarker function when the value is truly when an marker is active,
 * otherwise run setActiveMarker.
 */
const selectedMarker$ = store$.pipe(map(selectedMarker), distinctUntilChanged());

/**
 * Closes the marker card when the selectedMarker$ observable emits a value.
 * This is used to close any previously opened card.
 */
selectedMarker$.subscribe(closeMarkerCard);

/**
 * Subscribes to the `selectedMarker$` observable and opens a marker card when a marker with a valid ID is selected.
 * The marker icon element is extracted from the marker information and used to open the marker card.
 */
selectedMarker$
  .pipe(
    filter((marker) => Boolean(marker?.icon)),
    map((marker) => marker.icon.querySelector('[data-marker-icon]'))
  )
  .subscribe(openMarkerCard);

/**
 * Subscribing to the store$ observable to listen for changes.
 * When the map coordinates have changed, map the coordinates and compare if the value has changed.
 * If that is the case run the handleMarkerMapChanges function.
 */
store$.pipe(map(geoCoordinates), distinctUntilChanged()).subscribe(handleMarkerMapChanges);

store$.pipe(map(selectedMarker), distinctUntilChanged()).subscribe(handleSelectedMarker);

/**
 * By subscribing to selectFocusedListing, we can effectively monitor changes in the Point of Interest listing.
 * This enables us to dynamically apply styles to the relevant marker, providing an intuitive visual indication
 * to the user regarding the location of a listing.
 */
store$
  .pipe(
    map(selectFocusedListingAndSitePlans),
    distinctUntilKeyChanged('focusedListing'),
    filter(({ focusedListing, sitePlans }) =>
      !sitePlans.some((sitePlan) => sitePlan.communityId === focusedListing?.communityId)),
    map(({ focusedListing }) => {
      const markers = document.querySelectorAll(`[data-geographic-key="${focusedListing.geographicKey}"]`);

      const focusedMarker = Array.from(markers).find(
        (pin) =>
          pin.getAttribute('data-id') === focusedListing.id ||
          pin.getAttribute('data-id') === focusedListing.communityId
      );

      const mainMarker = focusedMarker?.closest('[data-marker-element]');
      const tooltip =
        mainMarker?.querySelector(`[data-tooltip][data-id='${focusedListing.id}']`) ||
        mainMarker?.querySelector(`[data-tooltip][data-id='${focusedListing.communityId}']`);

      const marker = mainMarker?.querySelector('[data-marker-icon]');

      return {
        marker,
        tooltip,
      };
    }),
    tap(closeMarkerCard),
    filter(({ marker, tooltip }) => marker && tooltip),
    tap(setHoveredMarker),
    tap(prioritizeMarkerTooltip),
    map(({ marker }) => marker)
  )
  .subscribe(openMarkerCard);

/**
 * By subscribing to "selectHoveredMarker", we are able to find the previous and current hovered markers and apply
 * operations to them. The idea of the observable is to work with the hover functionality of listings cards that is
 * reflected in highlighting the related markers in the map.
 * The selector "selectHoveredMarker" provides an object representing a marker and a tooltip of the interested listing,
 * both subscriber and "tap" operators of the observable are meant to work on style adjustments of this marker
 * to represent accurate information to the user that matches with the hovered listing card.
 */
store$
  .pipe(
    map(selectHoveredMarker),
    distinctUntilChanged(),
    pairwise(),
    map((stateTransition) => ({
      previousMarkerInfo: stateTransition[0],
      currentMarkerInfo: stateTransition[1],
    })),
    tap(adjustPreviousHoveredMarkerStyle)
  )
  .subscribe(adjustCurrentHoveredMarkerStyle);

/**
 * An observable that emits when the map container is double-clicked, and the click target is an element with the
 * `data-marker-element` attribute.
 */
const markerDoubleClicked$ = mapContainerDoubleClicked$.pipe(
  filter((event) => event.target.closest('[data-marker-element]'))
);

/**
 * Listens for a double click event on the map container element and subscribes to the handleMarkerMapDoubleClick
 * function. This allows the application to handle double click events on map markers and open the associated URL.
 */

markerDoubleClicked$.subscribe(handleMarkerMapDoubleClick);
/**
 * Listening for a click event on the map.
 */
mapContainer.addEventListener('mapHide', handleMarkerMapChanges);

mapContainerClicked$
  .pipe(
    filter((event) => !isUserInteractingWithSitePlan(event)),
    filter(({ target }) => !target.closest('[data-marker-icon]'))
  )
  .subscribe(handleMarkerMapChanges);

/*
 * Observable to raise the fetchPageNumber when the selected marker changed and:
 * 1. Isn't a small layout.
 * 2. The feature of AutoScroll is enable.
 * 3. The selected marker has page number.
 * 4. And the page of selected marker is different to the current listing page.
 */
store$
  .pipe(
    filter((state) => selectLayoutBreakpoint(state).getValue() !== LayoutBreakpoint.Small.getValue()),
    filter((state) => selectResultType(state) === ResultType.Communities.toString()),
    filter((state) => selectIsMarkerAutoScrollFeatureEnabled(state) === true),
    map(selectedMarkerAndPage),
    distinctUntilKeyChanged('marker'),
    filter(({ marker }) => marker?.pageNumber),
    filter(({ marker, page }) => marker.pageNumber !== page)
  )
  .subscribe(({ marker }) => store.dispatch(searchParametersActions.fetchPageNumber(marker.pageNumber)));
